/**
 * Enhanced Error Toast Component
 * Displays error messages with severity levels and actions
 */

import { AlertCircle, AlertTriangle, Info, X, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

import { ErrorSeverity } from '@/lib/errorContextTypes';
import { cn } from '@/lib/utils';

interface ErrorToastProps {
  message: string;
  onClose: () => void;
  severity?: ErrorSeverity;
  suggestion?: string;
  action?: {
    label: string;
    handler: () => void;
  };
  duration?: number;
}

const severityStyles = {
  info: {
    bg: 'bg-blue-600',
    icon: Info,
    title: 'Info',
  },
  warning: {
    bg: 'bg-yellow-600',
    icon: AlertTriangle,
    title: 'Warning',
  },
  error: {
    bg: 'bg-red-600',
    icon: AlertCircle,
    title: 'Error',
  },
  critical: {
    bg: 'bg-red-800',
    icon: XCircle,
    title: 'Critical Error',
  },
};

export function ErrorToast({
  message,
  onClose,
  severity = 'error',
  suggestion,
  action,
  duration,
}: ErrorToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const style = severityStyles[severity];
  const Icon = style.icon;

  // Don't auto-hide errors and critical messages unless duration is explicitly set
  const autoHide = severity === 'info' || severity === 'warning' || duration !== undefined;
  const hideDelay = duration || (severity === 'info' || severity === 'warning' ? 5000 : 0);

  useEffect(() => {
    setIsVisible(true);

    if (autoHide && hideDelay > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onClose, 300); // Wait for fade out animation
      }, hideDelay);

      return () => clearTimeout(timer);
    }
  }, [hideDelay, onClose, autoHide]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  return (
    <div
      className={cn(
        'relative max-w-md text-white rounded-lg shadow-lg transform transition-all duration-300',
        style.bg,
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      )}
    >
      <div className="p-4">
        <div className="flex items-start space-x-3">
          <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold">{style.title}</p>
            <p className="text-sm mt-1">{message}</p>
            {suggestion && <p className="text-xs mt-2 opacity-90">{suggestion}</p>}
            {action && (
              <button
                onClick={() => {
                  action.handler();
                  handleClose();
                }}
                className="mt-2 text-xs font-medium bg-white/20 hover:bg-white/30 px-3 py-1 rounded transition-colors"
              >
                {action.label}
              </button>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-white/60 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
